import React, { useState, useEffect } from 'react';
import { FilterColumnProps } from '../types';
import { FilterOperator } from '../../../interfaces/sessions';

export function PercentageFilter({ value, onChange }: FilterColumnProps) {
  const [minValue, setMinValue] = useState<string>('');
  const [maxValue, setMaxValue] = useState<string>('');

  // Convert stored decimal values (0.00-1.00) to display percentages (0-100)
  const decimalToPercent = (decimal: number | null): string => {
    if (decimal === null || decimal === undefined) return '';
    return Math.round(decimal * 100).toString();
  };

  // Convert display percentage (0-100) to stored decimal (0.00-1.00)
  const percentToDecimal = (percent: string): number | null => {
    if (percent === '') return null;
    const numericValue = parseFloat(percent);
    if (isNaN(numericValue)) return null;
    // Constrain to 0-100 range and convert to decimal
    const constrainedPercent = Math.max(0, Math.min(100, numericValue));
    return constrainedPercent / 100;
  };

  // Initialize values from current filter state
  useEffect(() => {
    const currentValue = value.value_safe;
    
    // Check if this is the new combined filter format
    if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
      // Handle combined filter format with min/max properties
      const combinedData = currentValue as { min?: { operator: string, value: number }, max?: { operator: string, value: number } };
      
      if (combinedData.min) {
        setMinValue(decimalToPercent(combinedData.min.value));
      } else {
        setMinValue('');
      }
      
      if (combinedData.max) {
        setMaxValue(decimalToPercent(combinedData.max.value));
      } else {
        setMaxValue('');
      }
    } else {
      // Handle legacy single filter format
      const currentOperator = value.operator;

      if (currentOperator === FilterOperator.GE) {
        // This is a minimum value filter (>= X)
        setMinValue(decimalToPercent(currentValue as number));
        setMaxValue('');
      } else if (currentOperator === FilterOperator.LE) {
        // This is a maximum value filter (<= X)
        setMaxValue(decimalToPercent(currentValue as number));
        setMinValue('');
      } else if (currentOperator === FilterOperator.EQ) {
        // Exact match - set both min and max to same value
        const percentValue = decimalToPercent(currentValue as number);
        setMinValue(percentValue);
        setMaxValue(percentValue);
      } else if (currentOperator === FilterOperator.GT) {
        // Convert GT to GE for consistency
        setMinValue(decimalToPercent(currentValue as number));
        setMaxValue('');
      } else if (currentOperator === FilterOperator.LT) {
        // Convert LT to LE for consistency  
        setMaxValue(decimalToPercent(currentValue as number));
        setMinValue('');
      } else {
        // Clear both values for unknown operators
        setMinValue('');
        setMaxValue('');
      }
    }
  }, [value.value_safe, value.operator]);

  const handleMinChange = (newMinValue: string) => {
    setMinValue(newMinValue);
    updateFilter(newMinValue, maxValue, 'min');
  };

  const handleMaxChange = (newMaxValue: string) => {
    setMaxValue(newMaxValue);
    updateFilter(minValue, newMaxValue, 'max');
  };

  const updateFilter = (minPercent: string, maxPercent: string, lastChanged?: 'min' | 'max') => {
    const minDecimal = percentToDecimal(minPercent);
    const maxDecimal = percentToDecimal(maxPercent);
    
    // Create an array to hold the filters we want to create
    const filtersToCreate: any[] = [];
    
    // Add min filter if min value exists
    if (minDecimal !== null) {
      filtersToCreate.push({
        ...value,
        operator: FilterOperator.GE,
        value_safe: minDecimal
      });
    }
    
    // Add max filter if max value exists
    if (maxDecimal !== null) {
      filtersToCreate.push({
        ...value,
        operator: FilterOperator.LE,
        value_safe: maxDecimal
      });
    }
    
    // For the PercentageFilter component, we can only call onChange once
    // So we need to send a special signal to the parent component
    // We'll use a special format that the parent can recognize
    if (filtersToCreate.length === 0) {
      // No filters - send clear signal to remove all percentage filters
      onChange({
        ...value,
        operator: FilterOperator.EQ, // Placeholder
        value_safe: {
          __clearAllFilters: true
        }
      });
    } else if (filtersToCreate.length === 1) {
      // Single filter - send it directly
      onChange(filtersToCreate[0]);
    } else {
      // Multiple filters - we need a way to send both
      // We'll use a special object that the parent can recognize
      onChange({
        ...value,
        operator: FilterOperator.EQ, // Placeholder
        value_safe: {
          __multipleFilters: true,
          filters: filtersToCreate
        }
      });
    }
  };

  return (
    <div className="w-full space-y-2">
      {/* Min Percentage Input */}
      <div className="relative">
        <input
          type="number"
          value={minValue}
          onChange={(e) => handleMinChange(e.target.value)}
          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Min %"
          min="0"
          max="100"
          step="1"
          style={{
            fontSize: '13px',
            fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
          }}
        />
        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
          %
        </span>
      </div>

      {/* Max Percentage Input */}
      <div className="relative">
        <input
          type="number"
          value={maxValue}
          onChange={(e) => handleMaxChange(e.target.value)}
          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Max %"
          min="0"
          max="100"
          step="1"
          style={{
            fontSize: '13px',
            fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
          }}
        />
        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
          %
        </span>
      </div>

      {/* Clear button when values are set */}
      {(minValue || maxValue) && (
        <button
          onClick={() => {
            setMinValue('');
            setMaxValue('');
            // Send a special clear signal to remove all percentage filters for this column
            onChange({
              ...value,
              operator: FilterOperator.EQ, // Placeholder
              value_safe: {
                __clearAllFilters: true
              }
            });
          }}
          className="text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Clear percentage filter
        </button>
      )}
    </div>
  );
} 